import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { LogOut, Users, FileText, Bell, Search, Lock, Settings, UserPlus } from 'lucide-react';
import ChangePasswordModal from '@/components/emr/ChangePasswordModal';
import GlobalSearchBar from '@/components/emr/GlobalSearchBar';
import AppointmentCalendar from '@/components/emr/AppointmentCalendar';

const EMRDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showPasswordModal, setShowPasswordModal] = React.useState(false);
  const [stats, setStats] = React.useState({
    totalPatients: 0,
    visitsToday: 0,
    pendingFollowUps: 0
  });

  React.useEffect(() => {
    if (user) {
      fetchStats();
    }
  }, [user]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/emr/stats`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const scrollToSearch = () => {
    // Scroll to search bar
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // Focus on search input after scroll
    setTimeout(() => {
      const searchInput = document.querySelector('input[placeholder*="Search patients"]');
      if (searchInput) searchInput.focus();
    }, 300);
  };

  if (!user) {
    navigate('/');
    return null;
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* EMR Header */}
      <div className="bg-gray-900 border-b border-yellow-500/20">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img
                src="https://customer-assets.emergentagent.com/job_elite-client-growth/artifacts/l293bfcb_Eliteiv%20logo%20No%20Number%20or%20website.png"
                alt="EliteIV"
                className="h-12"
              />
              <div>
                <h1 className="text-2xl font-bold text-yellow-400">EliteIV EMR</h1>
                <p className="text-sm text-gray-400">Electronic Medical Records</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-white">{user.full_name}</p>
                <p className="text-xs text-gray-400 capitalize">{user.role}</p>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowPasswordModal(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                  title="Change Password"
                >
                  <Lock className="w-4 h-4" />
                </button>
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* EMR Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className="bg-gradient-to-br from-yellow-400/10 to-transparent border border-yellow-500/20 rounded-2xl p-8 mb-8">
          <h2 className="text-3xl font-bold text-yellow-400 mb-2">Welcome back, {user.full_name}!</h2>
          <p className="text-gray-300">Your EMR dashboard is ready. Start managing patient records.</p>
        </div>

        {/* Global Search Bar */}
        <div className="mb-8 flex justify-center">
          <GlobalSearchBar />
        </div>

        {/* APPOINTMENT CALENDAR - TOP SECTION */}
        <div className="mb-8">
          <AppointmentCalendar />
        </div>

        {/* Quick Stats - Matches your sketch */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gray-900 border border-yellow-500/20 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <Users className="w-8 h-8 text-yellow-400" />
              <span className="text-3xl font-bold text-white">{stats.totalPatients}</span>
            </div>
            <h3 className="text-gray-400 font-medium">Total Patients</h3>
          </div>
          <div className="bg-gray-900 border border-yellow-500/20 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <FileText className="w-8 h-8 text-yellow-400" />
              <span className="text-3xl font-bold text-white">{stats.visitsToday}</span>
            </div>
            <h3 className="text-gray-400 font-medium">Visits Today</h3>
          </div>
          <div className="bg-gray-900 border border-yellow-500/20 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <Bell className="w-8 h-8 text-yellow-400" />
              <span className="text-3xl font-bold text-white">{stats.pendingFollowUps}</span>
            </div>
            <h3 className="text-gray-400 font-medium">Pending Follow-Ups</h3>
          </div>
        </div>

        {/* Main Actions */}
        <div className="bg-gray-900 border border-yellow-500/20 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-yellow-400 mb-6">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button 
              onClick={() => navigate('/emr/patient/new')}
              className="bg-gradient-to-br from-yellow-400/20 to-transparent border border-yellow-500/30 rounded-xl p-6 text-left hover:from-yellow-400/30 transition-all"
            >
              <h4 className="text-xl font-bold text-yellow-400 mb-2">New Patient Intake</h4>
              <p className="text-gray-400">Register a new patient and create their medical record</p>
            </button>
            <button 
              onClick={scrollToSearch}
              className="bg-gradient-to-br from-yellow-400/20 to-transparent border border-yellow-500/30 rounded-xl p-6 text-left hover:from-yellow-400/30 transition-all"
            >
              <h4 className="text-xl font-bold text-yellow-400 mb-2">Search Patients</h4>
              <p className="text-gray-400">Use the search bar above to find patients by name, DOB, or phone</p>
            </button>
            <button 
              onClick={() => navigate('/emr/visit/document')}
              className="bg-gradient-to-br from-yellow-400/20 to-transparent border border-yellow-500/30 rounded-xl p-6 text-left hover:from-yellow-400/30 transition-all"
            >
              <h4 className="text-xl font-bold text-yellow-400 mb-2">Document Visit</h4>
              <p className="text-gray-400">Record IV therapy session and treatment details</p>
            </button>
            <button 
              onClick={() => navigate('/emr/followups')}
              className="bg-gradient-to-br from-red-400/20 to-transparent border-2 border-red-500/50 rounded-xl p-6 text-left hover:from-red-400/30 transition-all relative overflow-hidden"
            >
              <div className="absolute top-2 right-2 w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
              <h4 className="text-xl font-bold text-red-400 mb-2">Follow-Up Calls</h4>
              <p className="text-gray-400">View and complete next-day follow-up tasks</p>
            </button>
            {user.role === 'admin' && (
              <button 
                onClick={() => navigate('/emr/users')}
                className="bg-gradient-to-br from-blue-400/20 to-transparent border border-blue-500/30 rounded-xl p-6 text-left hover:from-blue-400/30 transition-all"
              >
                <h4 className="text-xl font-bold text-blue-400 mb-2 flex items-center">
                  <UserPlus className="w-5 h-5 mr-2" />
                  User Management
                </h4>
                <p className="text-gray-400">Create employee accounts and manage access (Admin Only)</p>
              </button>
            )}
          </div>
        </div>
      </div>

      <ChangePasswordModal isOpen={showPasswordModal} onClose={() => setShowPasswordModal(false)} />
    </div>
  );
};

export default EMRDashboard;